import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Ban } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { browseHrefForKey } from '../../lib/wiki';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

type BlockedRow = {
  id: string;
  key: string;
  title: string;
  blocked_reason: string | null;
  blocked_at: string | null;
};

const SHOWN = 5;

/** How long this has been waiting, from the calendar day it was blocked. */
function waitingText(iso: string): string {
  const start = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start)) return '';
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.max(0, Math.round((today - start) / 86_400_000));
  if (days <= 0) return 'Today';
  return days === 1 ? '1 day' : `${days} days`;
}

/** Items someone marked as waiting on the signed-in person. Refetches when `refreshKey` changes. */
export function useBlockedByMe(refreshKey?: unknown): BlockedRow[] | null {
  const [items, setItems] = useState<BlockedRow[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ items: BlockedRow[] }>(`${v1Path('/work-items')}?blocked_by_ids=me&limit=100&offset=0`)
      .then((res) => { if (!cancelled) setItems(Array.isArray(res.items) ? res.items : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [refreshKey]);
  return items;
}

/** Home page card; renders nothing when nothing is waiting on you. */
export default function BlockedByYouCard({ refreshKey }: { refreshKey?: unknown }) {
  const items = useBlockedByMe(refreshKey);
  if (!items || items.length === 0) return null;
  return (
    <Card className="border-red-400 bg-red-100 shadow-sm shadow-red-950/10 dark:border-red-800 dark:bg-red-950/80 dark:shadow-none">
      <CardHeader className="flex-row items-center space-y-0 px-3 py-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-red-950 dark:text-red-50">
          <Ban className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden />
          Blocked on you
          <span className="rounded-full bg-red-700 px-1.5 py-0.5 text-[11px] font-semibold leading-4 tabular-nums text-white">{items.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-1.5 pb-1.5 pt-0">
        <ul className="space-y-0.5">
          {items.slice(0, SHOWN).map((row) => {
            const waiting = row.blocked_at ? waitingText(row.blocked_at) : '';
            return (
              <li key={row.id}>
                <Link
                  to={browseHrefForKey(row.key)}
                  className="group flex items-center gap-2 rounded-md bg-red-200/70 px-2 py-1.5 hover:bg-red-200 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 dark:bg-red-900/60 dark:hover:bg-red-900"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="font-mono text-xs text-red-800/80 dark:text-red-200/80">{row.key}</span>
                      <span className="truncate text-sm font-semibold text-red-950 group-hover:underline dark:text-red-50">{row.title}</span>
                    </span>
                    {row.blocked_reason ? (
                      <span className="block truncate text-xs font-medium text-red-800 dark:text-red-200">{row.blocked_reason}</span>
                    ) : null}
                  </span>
                  {waiting ? (
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-red-700 dark:text-red-300">{waiting}</span>
                  ) : null}
                  <ArrowRight className="h-4 w-4 shrink-0 text-red-700 transition-transform duration-150 group-hover:translate-x-0.5 dark:text-red-300" aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
        {items.length > SHOWN ? (
          <p className="px-2 pb-1 text-xs font-medium text-red-800 dark:text-red-200">And {items.length - SHOWN} more.</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
